import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  PackageCheck,
  Plus,
  RefreshCcw,
  Warehouse,
  Boxes,
  Truck,
  FileSpreadsheet,
  History,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { warehouseService } from "../../../services/warehouse.service";
import { inventoryService } from "../../../services/inventory.service";

import {
  ActionButton,
  Card,
  SearchInput,
  SelectLike,
  SummaryCard,
  TabButton,
  Pagination,
} from "./components/inventory-ui";
import AssignProductsModal from "./components/AssignProductsModal";
import ConfirmInboundModal from "./components/ConfirmInboundModal";
import InboundDetailsModal from "./components/InboundDetailsModal";
import WarehouseOrdersTab from "./components/WarehouseOrdersTab";
import InboundReceiptsTab from "./components/InboundReceiptsTab";

function normalizeList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.rows)) return res.rows;
  return [];
}

function getReadableWarehouseOrderId(order) {
  return (
    order?.displayOrderId ||
    order?.orderNo ||
    order?.orderNumber ||
    order?.code ||
    order?.referenceNo ||
    order?.id ||
    "-"
  );
}

function formatNumber(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0";
  return new Intl.NumberFormat("en-IN").format(num);
}

function getOrderItemKey(item) {
  return String(
    item?.id ||
      item?.orderItemId ||
      [item?.productId, item?.productVariantId || "default"].filter(Boolean).join(":")
  );
}

export default function AdminInventory() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("warehouseOrders");

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});

  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [orderStatus, setOrderStatus] = useState("pending");
  const [search, setSearch] = useState("");

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [savingDecision, setSavingDecision] = useState(false);

  const [stockItems, setStockItems] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [selectedStockProductId, setSelectedStockProductId] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const [inboundItems, setInboundItems] = useState([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [stockPage, setStockPage] = useState(1);
  const [inboundPage, setInboundPage] = useState(1);

  const ORDERS_PAGE_SIZE = 6;
  const STOCK_PAGE_SIZE = 8;
  const INBOUND_PAGE_SIZE = 8;

  const [toast, setToast] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [confirmReceipt, setConfirmReceipt] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsReceipt, setDetailsReceipt] = useState(null);

  const loadMasters = async () => {
    try {
      const warehouseRes = await warehouseService.listWarehouses({
        page: 1,
        limit: 200,
      });

      const warehouseItems = normalizeList(warehouseRes);

      setWarehouses(warehouseItems);
      setWarehouseMap(
        Object.fromEntries(
          warehouseItems.map((w) => [w.id, w.name || w.code || w.id])
        )
      );
    } catch (e) {
      setToast(e?.message || "Failed to load master data");
    }
  };

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);
      setOrdersError("");
      const res = await inventoryService.listWarehouseOrders({
        warehouseId: warehouseFilter,
        status: orderStatus,
        page: 1,
        limit: 100,
      });
      const items = normalizeList(res);
      setOrders(items);

      if (!selectedOrderId && items[0]?.id) setSelectedOrderId(items[0].id);
      if (selectedOrderId && !items.find((x) => x.id === selectedOrderId)) {
        setSelectedOrderId(items[0]?.id || "");
      }
    } catch (e) {
      setOrdersError(e?.message || "Failed to load orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadOrderDetails = async (orderId) => {
    if (!orderId) {
      setSelectedOrder(null);
      return;
    }
    try {
      const res = await inventoryService.getWarehouseOrder(orderId);
      const order = res?.order || res?.data?.order || res;
      setSelectedOrder(order);

      const defaults = {};
      (order?.items || []).forEach((item) => {
        const lineKey = getOrderItemKey(item);
        defaults[lineKey] = {
          itemId: item.id || item.orderItemId || null,
          productId: item.productId,
          productVariantId: item.productVariantId || null,
          status:
            item.status === "approved" || item.status === "rejected"
              ? item.status
              : "approved",
          approvedQty: item.approvedQty ?? item.requestedQty ?? "",
          approvedPurchasePrice:
            item.approvedPurchasePrice === null ||
              item.approvedPurchasePrice === undefined
              ? ""
              : String(item.approvedPurchasePrice),
          purchaseCurrency: item.purchaseCurrency || "INR",
          reason: item.reason || item.rejectReason || item.reject_reason || "",
        };
      });
      setDecisions(defaults);
    } catch (e) {
      setToast(e?.message || "Failed to load order details");
    }
  };

  const loadStock = async () => {
    try {
      setStockLoading(true);

      const params = {
        page: 1,
        limit: 100,
      };

      if (warehouseFilter) {
        params.warehouseId = warehouseFilter;
      }

      const res = await inventoryService.listStockSummary(params);
      const items = normalizeList(res);
      setStockItems(items);

      if (!items.length) {
        setStockItems([]);
        setSelectedStockProductId("");
        setBatches([]);
        return;
      }

      if (!selectedStockProductId) {
        setSelectedStockProductId(items[0]?.productId || "");
      } else if (!items.some((x) => x.productId === selectedStockProductId)) {
        setSelectedStockProductId(items[0]?.productId || "");
      }
    } catch (e) {
      setToast(e?.message || "Failed to load stock summary");
      setStockItems([]);
    } finally {
      setStockLoading(false);
    }
  };

  const loadBatches = async () => {
    if (!warehouseFilter || !selectedStockProductId) {
      setBatches([]);
      return;
    }
    try {
      setBatchesLoading(true);
      const res = await inventoryService.listBatches({
        warehouseId: warehouseFilter,
        productId: selectedStockProductId,
      });
      setBatches(normalizeList(res));
    } catch (e) {
      setToast(e?.message || "Failed to load batches");
    } finally {
      setBatchesLoading(false);
    }
  };

  const loadInbound = async () => {
    try {
      setInboundLoading(true);
      const res = await inventoryService.listInboundReceipts({
        warehouseId: warehouseFilter,
        page: 1,
        limit: 100,
      });
      setInboundItems(normalizeList(res));
    } catch (e) {
      setToast(e?.message || "Failed to load inbound receipts");
    } finally {
      setInboundLoading(false);
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    loadOrders();
    loadInbound();
  }, [warehouseFilter, orderStatus]);

  useEffect(() => {
    loadOrderDetails(selectedOrderId);
  }, [selectedOrderId]);

  useEffect(() => {
    loadStock();
  }, [warehouseFilter]);

  useEffect(() => {
    loadBatches();
  }, [warehouseFilter, selectedStockProductId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const visibleOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orders;

    return orders.filter((order) => {
      const warehouseName = warehouseMap[order.warehouseId] || order.warehouseId || "";
      return [
        order.id,
        getReadableWarehouseOrderId(order),
        warehouseName,
        order.status,
        order.notes,
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [orders, search, warehouseMap]);

  const summary = useMemo(() => {
    const pendingOrders = orders.filter(
      (x) => String(x.status).toLowerCase() === "pending"
    ).length;

    const approvedUnits = stockItems.reduce(
      (sum, item) => sum + Number(item.qtyAvailable || 0),
      0
    );

    const pendingInbound = inboundItems.filter(
      (x) => String(x.status).toLowerCase() === "pending_batch"
    ).length;

    return {
      pendingOrders,
      approvedUnits,
      pendingInbound,
      totalOrders: orders.length,
    };
  }, [orders, stockItems, inboundItems]);

  const totalOrdersPages = Math.max(
    1,
    Math.ceil(visibleOrders.length / ORDERS_PAGE_SIZE)
  );

  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ORDERS_PAGE_SIZE;
    return visibleOrders.slice(start, start + ORDERS_PAGE_SIZE);
  }, [visibleOrders, ordersPage]);

  const visibleStockItems = stockItems;

  const totalStockPages = Math.max(
    1,
    Math.ceil(visibleStockItems.length / STOCK_PAGE_SIZE)
  );

  const paginatedStockItems = useMemo(() => {
    const start = (stockPage - 1) * STOCK_PAGE_SIZE;
    return visibleStockItems.slice(start, start + STOCK_PAGE_SIZE);
  }, [visibleStockItems, stockPage]);

  const totalInboundPages = Math.max(
    1,
    Math.ceil(inboundItems.length / INBOUND_PAGE_SIZE)
  );

  const paginatedInboundItems = useMemo(() => {
    const start = (inboundPage - 1) * INBOUND_PAGE_SIZE;
    return inboundItems.slice(start, start + INBOUND_PAGE_SIZE);
  }, [inboundItems, inboundPage]);

  useEffect(() => {
    setOrdersPage(1);
  }, [search, warehouseFilter, orderStatus]);

  useEffect(() => {
    if (ordersPage > totalOrdersPages) {
      setOrdersPage(totalOrdersPages);
    }
  }, [ordersPage, totalOrdersPages]);

  useEffect(() => {
    setStockPage(1);
  }, [warehouseFilter]);

  useEffect(() => {
    if (stockPage > totalStockPages) {
      setStockPage(totalStockPages);
    }
  }, [stockPage, totalStockPages]);

  useEffect(() => {
    setInboundPage(1);
  }, [warehouseFilter, orderStatus]);

  useEffect(() => {
    if (inboundPage > totalInboundPages) {
      setInboundPage(totalInboundPages);
    }
  }, [inboundPage, totalInboundPages]);

  const updateDecision = (productId, patch) => {
    setDecisions((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], ...patch },
    }));
  };

  const submitDecisions = async () => {
    if (!selectedOrder?.id) return;

    const normalizedDecisions = [];

    for (const item of selectedOrder.items || []) {
      const lineKey = getOrderItemKey(item);
      const current = decisions[lineKey] || {};
      const status = current.status || "approved";
      const productName = item.productName || "Product";

      const approvedQty = Number(current.approvedQty || item.requestedQty || 0);
      const priceText = String(current.approvedPurchasePrice ?? "").trim();
      const approvedPurchasePrice = Number(priceText);

      if (status === "approved") {
        if (!Number.isFinite(approvedQty) || approvedQty <= 0) {
          setToast(`Approved qty is required for ${productName}`);
          return;
        }

        if (priceText === "" || !Number.isFinite(approvedPurchasePrice) || approvedPurchasePrice <= 0) {
          setToast(`Purchase price is required for ${productName}`);
          return;
        }
      }

      normalizedDecisions.push({
        itemId: item.id || current.itemId || null,
        productId: item.productId,
        productVariantId: item.productVariantId || current.productVariantId || null,
        status,
        ...(status === "approved"
          ? {
              approvedQty,
              approvedPurchasePrice,
              purchaseCurrency: current.purchaseCurrency || "INR",
            }
          : {}),
        ...(status === "rejected" && current.reason
          ? { reason: current.reason }
          : {}),
      });
    }

    try {
      setSavingDecision(true);

      const payload = {
        decisions: normalizedDecisions,
      };

      await inventoryService.decideWarehouseOrder(selectedOrder.id, payload);
      setToast("Order decision saved successfully");

      const savedOrderId = selectedOrder.id;

      await Promise.all([loadOrders(), loadInbound(), loadStock()]);

      if (String(orderStatus || "").toLowerCase() === "pending") {
        setSelectedOrderId("");
        setSelectedOrder(null);
        setDecisions({});
      } else {
        await loadOrderDetails(savedOrderId);
      }
    } catch (e) {
      setToast(e?.message || "Failed to save order decisions");
    } finally {
      setSavingDecision(false);
    }
  };

  const handleAssignProducts = async (payload) => {
    if (!payload?.warehouseId || !payload?.items?.length) {
      setToast("Please select warehouse and at least one product with qty");
      return;
    }

    try {
      setAssignSaving(true);
      await inventoryService.createInboundReceipt(payload);
      setToast("Products assigned successfully. Pending inbound receipt created.");
      setAssignOpen(false);

      if (payload.warehouseId !== warehouseFilter) {
        setWarehouseFilter(payload.warehouseId);
      }

      await Promise.all([loadInbound(), loadOrders(), loadStock()]);
    } catch (e) {
      setToast(e?.message || "Failed to assign products");
    } finally {
      setAssignSaving(false);
    }
  };

  const openConfirmInbound = async (receiptId) => {
    try {
      const res = await inventoryService.getInboundReceipt(receiptId);
      const receipt = res?.receipt || res?.data?.receipt || res;

      setConfirmReceipt(receipt);
      setConfirmOpen(true);
    } catch (e) {
      setToast(e?.message || "Failed to load receipt details");
    }
  };

  const openInboundDetails = async (receiptId) => {
    try {
      setDetailsLoading(true);
      const res = await inventoryService.getInboundReceipt(receiptId);
      const receipt = res?.receipt || res?.data?.receipt || res;
      setDetailsReceipt(receipt);
      setDetailsOpen(true);
    } catch (e) {
      setToast(e?.message || "Failed to load receipt details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const submitConfirmInbound = async (payload) => {
    if (!confirmReceipt?.id) return;

    const makeLocalDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTodayInputDate = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const today = makeLocalDate(getTodayInputDate());

for (const [index, item] of (payload.items || []).entries()) {
  const rowName = `Item ${index + 1}`;
  const mfgDate = makeLocalDate(item.mfgDate);
  const expiryDate = makeLocalDate(item.expiryDate);

  if (String(item.batchNo || "").trim().length < 5) {
  setToast(`${rowName}: Batch No must be at least 5 characters`);
  return;
}

  if (String(item.batchNo || "").trim().length < 5) {
  setToast(`${rowName}: Batch No must be at least 5 characters`);
  return;
}

  if (!mfgDate) {
    setToast(`${rowName}: MFG Date is invalid`);
    return;
  }

  if (!expiryDate) {
    setToast(`${rowName}: Expiry Date is invalid`);
    return;
  }

  if (today && mfgDate.getTime() > today.getTime()) {
    setToast(`${rowName}: MFG Date cannot be a future date`);
    return;
  }

  if (expiryDate.getTime() <= mfgDate.getTime()) {
    setToast(`${rowName}: Expiry Date must be after MFG Date`);
    return;
  }
}

    try {
      setConfirmSaving(true);
      await inventoryService.confirmInboundReceipt(confirmReceipt.id, payload);
      setToast("Inbound receipt confirmed successfully");
      setConfirmOpen(false);
      setConfirmReceipt(null);

      await Promise.all([loadInbound(), loadStock()]);
    } catch (e) {
      setToast(e?.message || "Failed to confirm inbound receipt");
    } finally {
      setConfirmSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-[30px] font-bold text-[#d86a78]">
            Inventory &amp; Warehouse Orders
          </h1>
        </div>

        <div className="flex flex-wrap gap-3">
          <ActionButton
            variant="soft"
            onClick={() => {
              loadOrders();
              loadInbound();
              loadStock();
            }}
          >
            <RefreshCcw size={16} /> Refresh
          </ActionButton>



          <ActionButton
            variant="soft"
            onClick={() => navigate("/admin/dashboard/catalog-import")}
          >
            <FileSpreadsheet size={16} />
            Assign Products to Warehouse
          </ActionButton>

          <ActionButton onClick={() => setAssignOpen(true)}>
            <Plus size={16} /> Transfer Stock
          </ActionButton>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={ClipboardList}
          title="Total Orders"
          value={summary.totalOrders}
          subtitle="Warehouse request orders"
        />
        <SummaryCard
          icon={PackageCheck}
          title="Pending Orders"
          value={summary.pendingOrders}
          subtitle="Need admin decision"
        />
        <SummaryCard
          icon={Boxes}
          title="Assigned Qty"
          value={formatNumber(summary.approvedUnits)}
          subtitle="Visible stock in selected warehouse"
        />
        <SummaryCard
          icon={Warehouse}
          title="Pending Inbound"
          value={summary.pendingInbound}
          subtitle="Warehouse confirmation pending"
        />
      </div>

      <Card className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px_220px]">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by warehouse, order id, status"
          />
          <SelectLike
            value={warehouseFilter}
            onChange={setWarehouseFilter}
            placeholder="All warehouses"
            options={warehouses.map((w) => ({
              value: w.id,
              label: w.name || w.code || w.id,
            }))}
          />
          <SelectLike
            value={orderStatus}
            onChange={setOrderStatus}
            placeholder="All statuses"
            options={[
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "partially_approved", label: "Partially Approved" },
            ]}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <TabButton
            active={activeTab === "warehouseOrders"}
            icon={ClipboardList}
            label="Warehouse Orders"
            onClick={() => setActiveTab("warehouseOrders")}
          />

          <TabButton
            active={activeTab === "inboundReceipts"}
            icon={Truck}
            label="Inbound Receipts"
            onClick={() => setActiveTab("inboundReceipts")}
          />

          <button
            type="button"
            onClick={() => navigate("/admin/dashboard/inventory/purchase-history")}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-[14px] border border-[#edd6da] bg-white px-5 text-[14px] font-semibold text-slate-700 transition hover:bg-[#fff7f8]"
          >
            <History size={16} />
            Purchase History
          </button>
        </div>
      </Card>

      {activeTab === "warehouseOrders" && (
        <WarehouseOrdersTab
          visibleOrders={paginatedOrders}
          ordersLoading={ordersLoading}
          ordersError={ordersError}
          selectedOrderId={selectedOrderId}
          setSelectedOrderId={setSelectedOrderId}
          selectedOrder={selectedOrder}
          warehouseMap={warehouseMap}
          decisions={decisions}
          updateDecision={updateDecision}
          submitDecisions={submitDecisions}
          savingDecision={savingDecision}
          pagination={
            <Pagination
              currentPage={ordersPage}
              totalPages={totalOrdersPages}
              onPageChange={setOrdersPage}
            />
          }
        />
      )}



      {activeTab === "inboundReceipts" && (
        <InboundReceiptsTab
          inboundLoading={inboundLoading}
          inboundItems={paginatedInboundItems}
          warehouseMap={warehouseMap}
          openInboundDetails={openInboundDetails}
          openConfirmInbound={openConfirmInbound}
          detailsLoading={detailsLoading}
          pagination={
            <Pagination
              currentPage={inboundPage}
              totalPages={totalInboundPages}
              onPageChange={setInboundPage}
            />
          }
        />
      )}

      <AssignProductsModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        warehouses={warehouses}
        onSubmit={handleAssignProducts}
        saving={assignSaving}
      />

      <ConfirmInboundModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmReceipt(null);
        }}
        receipt={confirmReceipt}
        warehouseName={
          warehouseMap[confirmReceipt?.warehouseId] || confirmReceipt?.warehouseId || "-"
        }
        onSubmit={submitConfirmInbound}
        saving={confirmSaving}
      />

      <InboundDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsReceipt(null);
        }}
        receipt={detailsReceipt}
        warehouseName={
          warehouseMap[detailsReceipt?.warehouseId] || detailsReceipt?.warehouseId || "-"
        }
      />

      {toast ? (
        <div className="fixed bottom-5 right-5 z-[999] rounded-[14px] bg-slate-900 px-4 py-3 text-[13px] font-semibold text-white shadow-xl">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
